import axios from 'axios';

/**
 * Normalize displayOrder values in an object or array.
 * - If displayOrder is 0 or "0", convert to 1
 * - All other values remain unchanged
 * - Handles nested objects and arrays recursively
 * - Does not mutate the original object (deep clone)
 */
const normalizeDisplayOrder = (data) => {
    if (data === null || data === undefined) {
        return data;
    }

    // Handle arrays - recursively normalize each element
    if (Array.isArray(data)) {
        return data.map(item => normalizeDisplayOrder(item));
    }

    // Handle objects - check for displayOrder and recursively process nested properties
    if (typeof data === 'object') {
        const normalized = { ...data };

        // Normalize displayOrder if it exists
        if (normalized.hasOwnProperty('displayOrder')) {
            const value = normalized.displayOrder;
            const numericValue = Number(value);
            // Check if value is 0 (either number or string "0")
            if (numericValue === 0 && value !== '') {
                normalized.displayOrder = 1;
            } else {
                normalized.displayOrder = value;
            }
        }

        // Recursively normalize all nested properties
        for (const key in normalized) {
            if (normalized.hasOwnProperty(key) && typeof normalized[key] === 'object') {
                normalized[key] = normalizeDisplayOrder(normalized[key]);
            }
        }

        return normalized;
    }

    // Return primitive values as-is
    return data;
};

// Global interceptor to inject the Authorization header
axios.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('accessToken');

        if (token) {
            // Set the Bearer token in the Authorization header
            config.headers['Authorization'] = `Bearer ${token}`;
        }

        // Ensure we always accept JSON
        if (!config.headers['accept']) {
            config.headers['accept'] = 'application/json';
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

/**
 * Handle responses globally, for example,
 * to redirect to login if the session expires (401).
 * Also normalizes displayOrder values to show 1 instead of 0.
 */
axios.interceptors.response.use(
    (response) => {
        // Normalize displayOrder values in the response data
        if (response.data) {
            response.data = normalizeDisplayOrder(response.data);
        }
        return response;
    },
    (error) => {
        // Normalize error object for callers
        if (error.response) {
            error.status = error.response.status;
            error.backendMessage = error.response.data?.message;
            error.apiPath = error.response.data?.path || error.config?.url;
        }

        // If the API returns 401 Unauthorized, we might want to clear local storage
        if (error.response && error.response.status === 401) {
            // Optional: Clear tokens and redirect to login if session has expired
            // localStorage.removeItem('accessToken');
            // localStorage.removeItem('isAuthenticated');
            // window.location.href = '/';
        }
        return Promise.reject(error);
    }
);

export default axios;
